// ============================================================
// TMR Portal Admin — moldura dos diálogos de um contrato já enviado
// ============================================================
import type { MouseEvent, ReactNode } from 'react';
import { Icons, StrokeIcon } from '../../lib/icons';
import { OverlayPortal } from '../../components/OverlayPortal';

/** Props for {@link ContractDialog}. */
interface ContractDialogProps {
  /** Small line above the title — which action this is. */
  eyebrow: string;
  title: string;
  children: ReactNode;
  /** The buttons, right-aligned. */
  footer: ReactNode;
  onClose: () => void;
}

/**
 * The frame shared by the dialogs that act on a contract already out for signature — the signing link
 * and the contact correction.
 *
 * **Rendered through `OverlayPortal` (a portal to `document.body`)**, for the reason `SendContractModal` documents: both are
 * opened from the contract detail, which lives inside `Drawer`, and `Drawer`'s `transform` would otherwise
 * box a `position: fixed` overlay into the side panel (`09` §11, N3).
 */
export function ContractDialog({ eyebrow, title, children, footer, onClose }: ContractDialogProps) {
  const stop = (e: MouseEvent) => e.stopPropagation();

  return (
    <OverlayPortal>
      <div onClick={onClose} role="dialog" aria-modal="true" aria-label={title} style={overlayStyle}>
        <div onClick={stop} style={panelStyle}>
          <header style={headerStyle}>
            <div>
              <div className="pa-eyebrow" style={{ marginBottom: 4 }}>
                {eyebrow}
              </div>
              <h3 style={titleStyle}>{title}</h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="pa-icon-btn"
              style={{ width: 34, height: 34 }}
              aria-label="Fechar"
            >
              <StrokeIcon d={Icons.x} size={18} />
            </button>
          </header>
          <div style={bodyStyle}>{children}</div>
          <footer style={footerStyle}>{footer}</footer>
        </div>
      </div>
    </OverlayPortal>
  );
}

const overlayStyle = {
  position: 'fixed' as const,
  inset: 0,
  zIndex: 70,
  background: 'rgba(28,30,28,0.32)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};

const panelStyle = {
  width: 560,
  maxWidth: '100%',
  maxHeight: '88vh',
  background: 'var(--cream-50)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-lg)',
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'hidden',
};

const headerStyle = {
  padding: '20px 24px',
  borderBottom: '1px solid var(--border-default)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 16,
};

const titleStyle = {
  fontFamily: 'var(--font-display)',
  fontSize: 22,
  margin: 0,
  letterSpacing: '-0.02em',
};

const bodyStyle = {
  flex: 1,
  overflowY: 'auto' as const,
  padding: '18px 24px 22px',
  fontSize: 13.5,
  lineHeight: 1.55,
  color: 'var(--fg-muted)',
};

const footerStyle = {
  padding: '14px 24px',
  borderTop: '1px solid var(--border-default)',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: 10,
};
